/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Input,
  OnInit,
  Signal,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { Granularities } from '@scenario/enums/granularities.enum';
import { MarketArea } from '@scenario/models/market-area.model';
import { Basecase } from '@scenario/models/basecase.model';
import { BasecaseGroup } from '@scenario/models/basecase-group.model';
import { Source } from '@scenario/models/source.model';
import { Subtype } from '@scenario/models/subtype.model';
import { WeatherYear } from '@scenario/models/weather-year.model';
import { Zone } from '@scenario/models/zone.model';
import {
  enableAdvancedMode,
  resetChartMarkersConfiguration,
  saveSourceChartMarker,
  saveZoneChartMarker,
  selectChartMarkersConfiguration,
} from '@store/scenario/app/app-slice';
import { dispatch, select } from '@store/injectables';
import {
  clearScenarios,
  retrieveScenariosByFilters,
} from '@store/scenario/scenarios/scenarios-slice';
import { unselectAllScenarios } from '@store/scenario/scenarios/selected-scenarios-slice';
import {
  retrieveFilteredReferentialsIds,
} from '@store/scenario/filtered-referentials/filtered-referentials-ids-slice';
import { Filters } from '@scenario/enums/filters.enum';
import { InsightService } from '@app/services/insight.service';
import { ChartMarkerConfiguration } from '@scenario/models/markers.model';
import { ChartMarkers } from '@scenario/enums/chart/chart-markers.enum';
import { scenariosWhiteList } from '@configurations/scenario/scenarios-white-list.configuration';
import {
  UserPreferencesViewModel,
  saveUserPreferences,
  selectUserPreferencesViewModel,
} from '@store/scenario/user-preferences/user-preferences-slice';
import { UserPreferences } from '@scenario/models/user-preferences.model';
import { TitleCasePipe } from '@angular/common';
import { ButtonComponent, IconComponent, ToggleComponent } from '@totalenergiescode/tds-angular';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { InfoIconTooltipComponent } from '@app/tooltips/info-icon-tooltip/info-icon-tooltip.component';
import { MatSelect } from '@angular/material/select';
import { MatOptgroup, MatOption } from '@angular/material/core';
import { ChartMarkersComponent } from '../chart/chart-markers/chart-markers.component';
import { MatDivider } from '@angular/material/divider';
import {
  MatDateRangeInput,
  MatDateRangePicker,
  MatDatepickerToggle,
  MatEndDate,
  MatStartDate,
} from '@angular/material/datepicker';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-search-scenarios-form',
  templateUrl: './search-scenarios-form.component.html',
  styleUrl: './search-scenarios-form.component.scss',
  imports: [
    ToggleComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    InfoIconTooltipComponent,
    MatSelect,
    MatOption,
    ChartMarkersComponent,
    MatOptgroup,
    MatDivider,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDatepickerToggle,
    MatSuffix,
    MatDateRangePicker,
    ButtonComponent,
    MatTooltip,
    IconComponent,
    TitleCasePipe,
  ],
})
export class SearchScenariosFormComponent implements OnInit {
  dispatch = dispatch();
  userPreferences: Signal<UserPreferencesViewModel> = select(selectUserPreferencesViewModel);
  advancedMode = computed(() => this.userPreferences().userPreferences.advanced);
  chartMarkers: Signal<ChartMarkerConfiguration> = select(selectChartMarkersConfiguration);

  filteredReferentialsZoneIds = select(
    state => state.filteredReferentialsIds.filteredReferentialsZoneIds,
  );
  filteredReferentialsSourceIds = select(
    state => state.filteredReferentialsIds.filteredReferentialsSourceIds,
  );
  filteredReferentialsBasecaseIds = select(
    state => state.filteredReferentialsIds.filteredReferentialsBasecaseIds,
  );
  filteredReferentialsSubtypeIds = select(
    state => state.filteredReferentialsIds.filteredReferentialsSubtypeIds,
  );
  filteredReferentialsWeatherYearIds = select(
    state => state.filteredReferentialsIds.filteredReferentialsWeatherYearIds,
  );
  filteredReferentialsMarketAreaIds = select(
    state => state.filteredReferentialsIds.filteredReferentialsMarketAreaIds,
  );

  Granularities = Object.values(Granularities);

  @Input() public regions!: Zone[];
  @Input() public sources!: Source[];
  @Input() public basecases!: Basecase[];
  @Input() public subtypes!: Subtype[];
  @Input() public weatherYears!: WeatherYear[];
  @Input() public marketAreas: MarketArea[];

  public filteredmarketAreas: MarketArea[] = [];
  public filteredRegions: Zone[] = [];
  public filteredSources: Source[] = [];
  public sourcesGroupedByCategory: any[] = [];
  public filteredBasecases: Basecase[] = [];
  public basecaseGroups: BasecaseGroup[] = [];
  public filteredSubtypes: Subtype[] = [];
  public filteredWeatherYears: WeatherYear[] = [];

  searchScenariosForm = new FormGroup<any>({
    marketAreaIds: new FormControl(),
    zoneIds: new FormControl(),
    sourceIds: new FormControl(),
    basecaseIds: new FormControl(),
    subtypeIds: new FormControl(),
    weatherYearIds: new FormControl(),
    granularity: new FormControl(Granularities.Yearly),
    publicationDateType: new FormControl(null),
    startPublicationDate: new FormControl<Date | null>({ value: null, disabled: true }),
    endPublicationDate: new FormControl<Date | null>({ value: null, disabled: true }),
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly insightService: InsightService,
  ) {
    effect(() => {
      if (!this.advancedMode()) {
        this.searchScenariosForm.controls['granularity'].setValue(Granularities.Yearly);
        this.searchScenariosForm.controls['weatherYearIds'].setValue([
          this.weatherYears.find(wy => wy.name === 'AVERAGE')?.id,
        ]);
        this.searchScenariosForm.controls['publicationDateType'].patchValue('1');
        this.searchScenariosForm.controls['startPublicationDate'].disable();
        this.searchScenariosForm.controls['startPublicationDate'].patchValue(null);
        this.searchScenariosForm.controls['endPublicationDate'].disable();
        this.searchScenariosForm.controls['endPublicationDate'].patchValue(null);
        untracked(() => {
          this.dispatch(resetChartMarkersConfiguration());
        });
      }

      untracked(() => {
        this.handleChartMarkerRules();
        this.retrieveDynamicFiltersFromForm();
      });
    });

    effect(() => {
      this.filteredmarketAreas = this.filterEntities(this.marketAreas, this.filteredReferentialsMarketAreaIds());
      this.filteredRegions = this.filterEntities(this.regions, this.filteredReferentialsZoneIds());
      this.filteredSources = this.filterEntities(
        this.sources,
        this.filteredReferentialsSourceIds(),
      );
      const basecases: Basecase[] = this.filterEntities(
        this.basecases,
        this.filteredReferentialsBasecaseIds(),
      );
      this.filteredBasecases = this.advancedMode()
        ? basecases
        : basecases.filter(basecase => {
            const name = basecase.name.toLowerCase();
            return scenariosWhiteList.includes(name) || name.startsWith('plt26_');
          });
      this.basecaseGroups = this.groupBasecases(this.filteredBasecases);
      this.sourcesGroupedByCategory = this.transformSources(this.filteredSources);
      this.filteredSubtypes = this.filterEntities(
        this.subtypes,
        this.filteredReferentialsSubtypeIds(),
      );
      this.filteredWeatherYears = this.filterEntities(
        this.weatherYears,
        this.filteredReferentialsWeatherYearIds(),
      );
    });
  }

  ngOnInit() {
    const urlParamsSubscription = this.route.queryParamMap.subscribe((params: ParamMap) => {
      if (params.keys.length > 0) {
        this.mapUrlParamsToForm(params);
        this.searchScenarios();
      }
    });
    urlParamsSubscription.unsubscribe();
  }

  filterEntities = (entities: any[], ids: number[]) =>
    ids.length > 0 && undefined !== entities
      ? entities.filter(entity => ids.includes(entity.id))
      : entities;

  mapUrlParamsToForm(params: ParamMap) {
    if (params.get('advancedMode') === 'true') {
      this.dispatch(enableAdvancedMode());
    }

    ['marketAreaIds', 'zoneIds', 'sourceIds', 'basecaseIds', 'subtypeIds', 'weatherYearIds'].forEach(key => {
      if (params.has(key)) {
        const ids = params.get(key)?.split(',').map(Number);
        this.searchScenariosForm.controls[key].setValue(ids);
      }
    });

    if (params.has('granularity')) {
      this.searchScenariosForm.get('granularity')?.patchValue(params.get('granularity'));
    }

    if (params.has('publicationDateType')) {
      this.searchScenariosForm
        .get('publicationDateType')
        ?.patchValue(params.get('publicationDateType'));
    }

    if (params.has('startPublicationDate')) {
      const startPublicationDate = params.get('startPublicationDate');
      if (startPublicationDate) {
        this.searchScenariosForm
          .get('startPublicationDate')
          ?.patchValue(new Date(startPublicationDate));
      }
    }

    if (params.has('endPublicationDate')) {
      const endPublicationDate = params.get('endPublicationDate');
      if (endPublicationDate) {
        this.searchScenariosForm
          .get('endPublicationDate')
          ?.patchValue(new Date(endPublicationDate));
      }
    }
  }

  publicationDateTypeChange(publicationDateType: any) {
    if (Number(publicationDateType) === 3) {
      this.searchScenariosForm.controls['startPublicationDate'].enable();
      this.searchScenariosForm.controls['endPublicationDate'].enable();
    } else {
      this.searchScenariosForm.controls['startPublicationDate'].disable();
      this.searchScenariosForm.controls['startPublicationDate'].patchValue(null);
      this.searchScenariosForm.controls['endPublicationDate'].disable();
      this.searchScenariosForm.controls['endPublicationDate'].patchValue(null);
    }
  }

  setInsightSearchAttributes() {
    const formZoneIds = this.searchScenariosForm.controls['zoneIds'].value;
    const zoneFullNames: string[] = formZoneIds
      ? this.regions
          .filter(region => formZoneIds.includes(region.id))
          .map(region => region.name + ' - ' + region.alias)
      : [];

    const formSourceIds = this.searchScenariosForm.controls['sourceIds'].value;
    const sourceNames: string[] = formSourceIds
      ? this.sources.filter(source => formSourceIds.includes(source.id)).map(source => source.name)
      : [];

    const formBasecaseIds = this.searchScenariosForm.controls['basecaseIds'].value;
    const basecaseNames: string[] = formBasecaseIds
      ? this.basecases
          .filter(basecase => formBasecaseIds.includes(basecase.id))
          .map(basecase => basecase.name)
      : [];

    const formSubtypeIds = this.searchScenariosForm.controls['subtypeIds'].value;
    const subtypeNames: string[] = formSubtypeIds
      ? this.subtypes
          .filter(subtype => formSubtypeIds.includes(subtype.id))
          .map(subtype => subtype.name)
      : [];

    const formWeatherYearIds = this.searchScenariosForm.controls['weatherYearIds'].value;
    const weatherYearNames: string[] = formWeatherYearIds
      ? this.weatherYears
          .filter(weatheryear => formWeatherYearIds.includes(weatheryear.id))
          .map(weatheryear => weatheryear.name)
      : [];

    const isAdvancedModeSearch = this.advancedMode();
    this.insightService.addSearchAttributesAwsRum(
      zoneFullNames,
      sourceNames,
      basecaseNames,
      subtypeNames,
      weatherYearNames,
      isAdvancedModeSearch,
    );
  }

  searchScenarios() {
    if (this.searchScenariosForm.valid) {
      const formParams = this.searchScenariosForm.value;

      this.dispatch(unselectAllScenarios());

      this.setInsightSearchAttributes();

      this.insightService.recordEvent('search', {});

      this.dispatch(retrieveScenariosByFilters(formParams));

      this.router.navigate([], {
        queryParams: this.formatParamsForUrl({ ...formParams, advancedMode: this.advancedMode() }),
        queryParamsHandling: 'replace',
        relativeTo: this.route,
      });
    }
  }

  checkIfOnlyOneRegionIsSelected = () =>
    this.searchScenariosForm.controls['zoneIds'].value?.length === 1;

  applyMarkerRuleForOneCountrySelectedInSimpleMode = () => {
    if (this.checkIfOnlyOneRegionIsSelected()) {
      this.dispatch(resetChartMarkersConfiguration());
      this.dispatch(saveZoneChartMarker(ChartMarkers.NONE));
      this.dispatch(saveSourceChartMarker(ChartMarkers.COLOR));
    } else {
      this.dispatch(resetChartMarkersConfiguration());
    }
  };

  handleChartMarkerRules = () => {
    if (!this.advancedMode()) {
      this.applyMarkerRuleForOneCountrySelectedInSimpleMode();
    }
  };

  retrievereFilteredReferentialsIds = (changedFilter: Filters) => {
    if (changedFilter === Filters.REGION) {
      this.handleChartMarkerRules();
    }

    this.retrieveDynamicFiltersFromForm();
  };

  formatParamsForUrl(params: any): Params {
    const urlParams: Params = { ...params };
    ['marketAreaIds', 'zoneIds', 'sourceIds', 'basecaseIds', 'subtypeIds', 'weatherYearIds'].forEach(
      (key: string) => {
        if (params[key]?.length > 0) {
          urlParams[key] = params[key].join(',');
        }
      },
    );
    return urlParams;
  }

  displayedRegions = computed(() => {
    const filteredIds = this.filteredReferentialsZoneIds();
    const allZones = this.regions || [];
    return filteredIds.length > 0 ? allZones.filter(z => filteredIds.includes(z.id)) : allZones;
  });

  availableMarketAreas = computed(() => {
    const filteredIds = this.filteredReferentialsMarketAreaIds();
    const allAreas = this.marketAreas || [];
    return filteredIds.length > 0 ? allAreas.filter(a => filteredIds.includes(a.id)) : allAreas;
  });

  onSourceSelectionChange() {
    this.retrievereFilteredReferentialsIds(Filters.SOURCE);
  }

  clearAllFilters() {
    this.searchScenariosForm.reset();
    this.resetURLParams();
    const averageWeatherYearId = this.weatherYears.find(wy => wy.name === 'AVERAGE')?.id;
    const defaultWeatherYearIds = averageWeatherYearId === undefined ? [] : [averageWeatherYearId];
    this.dispatch(
      retrieveFilteredReferentialsIds({
        inactiveFields: ['market', 'zone', 'source', 'basecase', 'subtype', 'weather'],
        filteredReferentialsIds: {
          marketAreaIds: [],
          zoneIds: [],
          sourceIds: [],
          basecaseIds: [],
          subtypeIds: [],
          weatherYearIds: defaultWeatherYearIds,
        },
      }),
    );
    this.setDefaultFilters(defaultWeatherYearIds);
    this.insightService.recordEvent('clear', {});
    this.dispatch(clearScenarios());
  }

  setDefaultFilters(defaultWeatherYearIds: number[]) {
    this.searchScenariosForm.controls['granularity'].setValue(Granularities.Yearly);
    this.searchScenariosForm.controls['weatherYearIds'].setValue(defaultWeatherYearIds);
    this.searchScenariosForm.controls['publicationDateType'].setValue('1');
  }

  resetURLParams() {
    this.router.navigate([], {
      queryParams: null,
      relativeTo: this.route,
    });
  }

  retrieveDynamicFiltersFromForm = () => {
    const inactiveFields = ['market', 'zone', 'source', 'basecase', 'subtype', 'weather'];
    this.dispatch(
      retrieveFilteredReferentialsIds({
        inactiveFields,
        filteredReferentialsIds: {
          marketAreaIds: this.searchScenariosForm.controls['marketAreaIds'].value as number[],
          zoneIds: this.searchScenariosForm.controls['zoneIds'].value as number[],
          sourceIds: this.searchScenariosForm.controls['sourceIds'].value as number[],
          basecaseIds: this.searchScenariosForm.controls['basecaseIds'].value as number[],
          subtypeIds: this.searchScenariosForm.controls['subtypeIds'].value as number[],
          weatherYearIds: this.searchScenariosForm.controls['weatherYearIds'].value as number[],
        },
      }),
    );
  };

  toggleAdvancedMode(toggle: boolean) {
    if (toggle) {
      this.insightService.recordEvent('advanced', {});
    } else if (!toggle) {
      this.insightService.recordEvent('simple', {});
    }
    this.saveUserPreferencesAdvancedUpdated(toggle);
  }

  transformSources(sources: any[]): any[] {
    const categories: { [key: string]: Source[] } = {};

    sources.forEach(source => {
      const category: string = source.category;
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(source);
    });

    return Object.keys(categories).map(category => ({
      category: category,
      sources: categories[category],
    }));
  }

  groupBasecases(basecases: Basecase[]): BasecaseGroup[] {
    const groups: { [key: string]: number[] } = {};

    basecases.forEach(basecase => {
      const groupName = basecase.name.startsWith('PLT26_') ? 'LTP26 (Lastest)' : basecase.name;
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(basecase.id);
    });

    return Object.keys(groups).map(name => ({
      name,
      basecaseIds: groups[name],
    }));
  }

  getSelectedBasecaseGroupNames(): string[] {
    const selectedIds: number[] = this.searchScenariosForm.controls['basecaseIds'].value ?? [];
    return this.basecaseGroups
      .filter(group => group.basecaseIds.some(id => selectedIds.includes(id)))
      .map(group => group.name);
  }

  onBasecaseGroupSelectionChange(selectedGroupNames: string[]) {
    const allIds = this.basecaseGroups
      .filter(group => selectedGroupNames.includes(group.name))
      .flatMap(group => group.basecaseIds);
    this.searchScenariosForm.controls['basecaseIds'].setValue(allIds);
    this.retrievereFilteredReferentialsIds(Filters.BASECASE);
  }

  saveUserPreferencesAdvancedUpdated(advancedMode: boolean) {
    const userPreferences: UserPreferences = {
      unitId: this.userPreferences().userPreferences.unitId,
      unitCurrency: this.userPreferences().userPreferences.unitCurrency,
      unitTerm: this.userPreferences().userPreferences.unitTerm,
      unitRealYear: this.userPreferences().userPreferences.unitRealYear,
      advanced: advancedMode,
      display: this.userPreferences().userPreferences.display,
    };
    this.dispatch(saveUserPreferences(userPreferences));
  }

  protected readonly Filters = Filters;
}
